import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/store/auth";
import { authApi } from "@/lib/apiClient";
import { User as UserIcon, Lock, Sparkles, ChevronRight, ChevronLeft, Globe } from "lucide-react";
import { toast } from "sonner";
import brandLogo from "@/assets/logo.png";
import { LOCALES, useLocale, t } from "@/lib/i18n";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const setSession = useAuth((s) => s.setSession);
  const navigate = useNavigate();
  const [locale] = useLocale(); // re-render this component when locale changes

  const loginM = useMutation({
    mutationFn: ({ username, password }) => authApi.login(username, password),
    onSuccess: (resp) => {
      const expires_at =
        resp.expires_at != null
          ? new Date(resp.expires_at).getTime()
          : Date.now() + 4 * 60 * 60 * 1000;
      setSession({ token: resp.token, user: resp.user, expires_at });
      toast.success(t("Welcome, {{name}}", { name: resp.user?.username || username }));
      navigate("/");
    },
    onError: (err) => {
      toast.error(err?.message || t("Invalid credentials"));
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error(t("Username and password are required"));
      return;
    }
    loginM.mutate({ username: username.trim(), password });
  };

  return (
    <div lang={locale} className="min-h-screen bg-zinc-50 flex items-center justify-center px-6 py-10 font-sans">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_minmax(0,340px)] gap-10 items-center">
        {/* -- Left: sign-in card --------------------------------------- */}
        <div className="w-full">
          <div className="flex items-center justify-center lg:justify-start mb-8">
            <img
              src={brandLogo}
              alt="IoT Admin Hub"
              className="h-32 w-auto object-contain"
              data-testid="brand-logo-login"
            />
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <h1 className="font-heading text-lg font-bold tracking-tight">{t("Sign in")}</h1>
              <p className="text-sm text-zinc-500 mt-1">
                {t("Use your operator username & password. Sessions last 4 hours.")}
              </p>
            </div>
            <form onSubmit={submit} className="px-6 pb-6 pt-4 space-y-3" data-testid="login-form">
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
                  {t("Username")}
                </span>
                <div className="mt-1.5 flex items-center gap-2 border border-zinc-200 rounded-md bg-white focus-within:ring-1 focus-within:ring-[#3730A3] focus-within:border-[#3730A3] transition">
                  <span className="pl-3 text-zinc-400">
                    <UserIcon className="w-4 h-4" />
                  </span>
                  <input
                    data-testid="login-username"
                    className="flex-1 bg-transparent py-2.5 pr-3 text-sm font-mono outline-none"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin"
                    autoComplete="username"
                    autoFocus
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
                  {t("Password")}
                </span>
                <div className="mt-1.5 flex items-center gap-2 border border-zinc-200 rounded-md bg-white focus-within:ring-1 focus-within:ring-[#3730A3] focus-within:border-[#3730A3] transition">
                  <span className="pl-3 text-zinc-400">
                    <Lock className="w-4 h-4" />
                  </span>
                  <input
                    data-testid="login-password"
                    type="password"
                    className="flex-1 bg-transparent py-2.5 pr-3 text-sm font-mono outline-none"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
              </label>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loginM.isPending}
                className="w-full bg-[#3730A3] text-white rounded-md py-2.5 text-sm font-medium hover:bg-indigo-900 active:scale-[0.99] transition disabled:opacity-50"
              >
                {loginM.isPending ? t("Signing in…") : t("Sign in →")}
              </button>
              <div className="text-[11px] text-zinc-500 text-center pt-1">
                {t("Sessions stored locally · sent as {{header}} on every request.", {
                  header: "Authorization: Bearer",
                })
                  .split("Authorization: Bearer")
                  .flatMap((part, i, arr) => [
                    <React.Fragment key={`p-${i}`}>{part}</React.Fragment>,
                    i < arr.length - 1 ? (
                      <code key={`c-${i}`} className="font-mono">Authorization: Bearer</code>
                    ) : null,
                  ])}
              </div>
            </form>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-400 items-start">
            <div>
              <div className="text-zinc-500">{t("build")}</div>
              <div className="text-zinc-700 mt-1">1.6.0</div>
            </div>
            <div>
              <div className="text-zinc-500">{t("region")}</div>
              <div className="text-zinc-700 mt-1">eu-west-1</div>
            </div>
            <div>
              <div className="text-zinc-500">{t("env")}</div>
              <div className="text-zinc-700 mt-1">preview</div>
            </div>
            <LocaleSwitcher />
          </div>
        </div>

        {/* -- Right: marketing / what's new rail ----------------------- */}
        <WhatsNewPanel />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rotating "what's new" card. Content lives in /public/whats-new.json so the
// copy can be edited without a rebuild.
// ---------------------------------------------------------------------------
function WhatsNewPanel() {
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  // Fetch once on mount — cheap static asset, no auth needed.
  useEffect(() => {
    let cancelled = false;
    fetch("/whats-new.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((json) => { if (!cancelled) setData(json); })
      .catch(() => { /* silent — the marketing rail is non-critical */ });
    return () => { cancelled = true; };
  }, []);

  const items = data?.items || [];
  const rotationMs = data?.rotation_ms || 6000;

  // Auto-rotate.
  useEffect(() => {
    if (!items.length || paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), rotationMs);
    return () => clearInterval(t);
  }, [items.length, paused, rotationMs]);

  // Nothing to show yet (still loading or file missing) — collapse gracefully.
  if (!items.length) {
    return <div className="hidden lg:block" data-testid="whats-new-empty" />;
  }

  const cur = items[i];
  const prev = () => setI((v) => (v - 1 + items.length) % items.length);
  const next = () => setI((v) => (v + 1) % items.length);

  return (
    <aside
      className="hidden lg:flex flex-col relative bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm min-h-[420px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="whats-new-panel"
    >
      {/* Indigo header stripe */}
      <div className="bg-[#3730A3] text-white px-5 py-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-lime-300" strokeWidth={2.2} />
          <span className="text-[11px] font-mono uppercase tracking-widest">{t("What's new")}</span>
        </div>
        <span className="text-[10px] font-mono opacity-70">{i + 1} / {items.length}</span>
      </div>

      {/* Body — fade-swap on index change (key forces remount → animation) */}
      <div className="flex-1 flex flex-col justify-between p-5">
        <div key={`item-${i}`} className="whats-new-fade">
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
              cur.tag === "new"
                ? "bg-lime-100 text-lime-700"
                : "bg-indigo-100 text-indigo-700"
            }`}>
              {t(cur.tag || "update")}
            </span>
            {cur.date && (
              <span className="text-[10px] font-mono text-zinc-400">{cur.date}</span>
            )}
          </div>
          <h2 className="font-heading text-xl font-bold tracking-tight leading-snug mb-2">
            {cur.title}
          </h2>
          <p className="text-sm text-zinc-600 leading-relaxed">
            {cur.body}
          </p>
        </div>

        {/* Nav dots + arrows */}
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={prev}
            className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition"
            data-testid="whats-new-prev"
            aria-label={t("Previous highlight")}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {items.map((_, idx) => (
              <button
                type="button"
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Go to highlight ${idx + 1}`}
                data-testid={`whats-new-dot-${idx}`}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? "w-6 bg-[#3730A3]" : "w-1.5 bg-zinc-300 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={next}
            className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition"
            data-testid="whats-new-next"
            aria-label={t("Next highlight")}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Locale switcher pill — sits in the login footer next to build/region/env.
// Persists selection to localStorage via /lib/i18n.js.
// ---------------------------------------------------------------------------
function LocaleSwitcher() {
  const [locale, setLocaleFn] = useLocale();
  const [open, setOpen] = useState(false);
  const cur = LOCALES.find((l) => l.code === locale) || LOCALES[0];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-testid='locale-switcher']")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" data-testid="locale-switcher">
      <div className="text-zinc-500 inline-flex items-center gap-1">
        <Globe className="w-3 h-3" />
        lang
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-1 inline-flex items-center gap-1.5 text-zinc-700 hover:text-[#3730A3] transition font-mono uppercase tracking-widest"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="locale-switcher-toggle"
      >
        <span className="text-base leading-none" aria-hidden>{cur.flag}</span>
        <span>{cur.short}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-10 bottom-full mb-2 right-0 bg-white border border-zinc-200 rounded-md shadow-lg py-1 min-w-[140px]"
          data-testid="locale-switcher-menu"
        >
          {LOCALES.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                onClick={() => { setLocaleFn(l.code); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-mono inline-flex items-center gap-2 hover:bg-zinc-50 ${
                  l.code === locale ? "text-[#3730A3] font-semibold" : "text-zinc-700"
                }`}
                data-testid={`locale-option-${l.code}`}
              >
                <span className="text-base leading-none" aria-hidden>{l.flag}</span>
                <span className="uppercase tracking-widest">{l.short}</span>
                <span className="text-zinc-400 lowercase tracking-normal ml-1">{l.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
